import React, { useEffect, useRef, useState } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link, useLocation } from 'react-router-dom';
import { ListChecks, NotebookPen, RefreshCw, CalendarRange, CalendarClock, PhoneCall, Siren, LayoutDashboard, FileText, Wallet, Landmark, ArrowLeftRight, BellRing, Layers, Settings, FileStack, History, Users, BarChart3, FolderArchive, Clock, Plug, Newspaper, Mail, Briefcase, Scale, Inbox, Palmtree, UserCircle, GripVertical, Hourglass, MonitorSmartphone, Eye, KeyRound, ShieldCheck, PenLine, FileSignature } from 'lucide-react';
import { useAuth, api } from '../../context/AuthContext';
import { useFeatures } from '../../hooks/useFeatures';
import { useMenueZaehler } from './KanzleiLeiste';

// [pfad, titel, kurztext, icon, aktiv-regex, nur Vollzugriff, feature, nur Admin, zähler]
const FLAECHEN = [
  { id: 'eingang', titel: 'Dashboard', match: /^\/kanzlei\/eingang/, kacheln: [
    ['/kanzlei/eingang', 'Übersicht', 'Wochenplan und alles für heute', LayoutDashboard, /^\/kanzlei\/eingang\/?$/],
    ['/kanzlei/eingang/anfragen', 'Alle Anfragen', 'Registrierungen, Signaturen, Raten, Bewerbungen', Inbox, null, true],
    ['/kanzlei/nachrichten', 'Nachrichten', 'Ungelesenes im Postfach', Mail, /^$/, false, null, false, 'nachrichten'],
    ['/kanzlei/mandanten/offen', 'Nicht zugeordnet', 'Einträge einem Mandanten zuordnen', Users, /^$/, true, null, false, 'nicht_zugeordnet'],
  ] },
  { id: 'mein', titel: 'Mein Bereich', match: /^\/kanzlei\/(arbeitszeit|urlaub|profil)/, kacheln: [
    ['/kanzlei/arbeitszeit', 'Meine Arbeitszeit', 'Kommen, Gehen, Pausen erfassen', Clock],
    ['/kanzlei/urlaub', 'Urlaub & Abwesenheit', 'Beantragen und Vorschläge prüfen', Palmtree],
    ['/kanzlei/profil', 'Profil & Sicherheit', 'Konto und Zwei-Faktor-Anmeldung', UserCircle],
  ] },
  { id: 'planer', titel: 'Aufgaben & Termine', match: /^\/(kanzlei\/(planer|termine|fristen|rueckrufe|kalender|outlook)|team\/(rueckrufe|tickets))/, kacheln: [
    ['/kanzlei/planer', 'Heute & Woche', 'Ihr Tag auf einen Blick', CalendarRange, null, true],
    ['/team/tickets', 'Aufgaben', 'Offene Aufgaben bearbeiten', ListChecks, /^\/team\/tickets/],
    ['/kanzlei/termine', 'Termine', 'Anlegen, verschieben, bestätigen', CalendarClock, null, true],
    ['/kanzlei/fristen', 'Fristenkalender', 'Alle Fristen aller Akten mit Ampel', Hourglass, null, true],
    ['/kanzlei/kalender', 'Gesprächsnotizen', 'Notizen zu Gesprächen', NotebookPen, null, true],
    ['/team/rueckrufe', 'Rückrufe', 'Rückrufwünsche abarbeiten', PhoneCall, /^\/team\/rueckrufe/],
    ['/kanzlei/outlook', 'Outlook-Abgleich', 'Kalender abgleichen', RefreshCw, null, true, null, false, 'outlook'],
    ['/kanzlei/rueckrufe/notfall', 'Notfall-Rückrufe', 'Notfälle sofort sehen', Siren, null, true, 'notfall'],
  ] },
  { id: 'support', titel: 'Mandanten-Support', match: /^\/kanzlei\/(support|cobrowsing|portalansicht|konten|signaturen|mandatsunterlagen)/, kacheln: [
    ['/kanzlei/support', 'Übersicht', 'Überfällige Unterlagen, Bestätigungen, Unterschriften', LayoutDashboard, /^\/kanzlei\/support\/?$/, true, null, false, 'support_unterlagen'],
    ['/kanzlei/cobrowsing', 'Co-Browsing', 'Gemeinsam auf den Bildschirm des Mandanten schauen', MonitorSmartphone, null, true],
    ['/kanzlei/portalansicht', 'Portalansicht', 'Das Portal sehen, wie der Mandant es sieht', Eye, null, true],
    ['/kanzlei/konten', 'Portalzugänge', 'Konten, Einladungen und Zugangsstatus', KeyRound, null, true],
    ['/kanzlei/support/bestaetigungen', 'AGB-Bestätigungen', 'Offene Erstbestätigungen, Erinnerung, Nachweis-PDF', ShieldCheck, null, true],
    ['/kanzlei/mandatsunterlagen', 'Unterschrift anfordern', 'Vollmacht und Mandatsunterlagen zur Signatur senden', PenLine, null, true],
    ['/kanzlei/signaturen', 'Unterschriften', 'Stand aller Signaturen verfolgen', FileSignature, null, true],
    ['/team/rueckrufe', 'Rückrufe', 'Rückrufwünsche abarbeiten', PhoneCall, /^$/, false, null, false, 'support_rueckrufe'],
    ['/kanzlei/rueckrufe/notfall', 'Notfälle', 'Notfall-Rückrufe sofort sehen', Siren, /^$/, true, 'notfall'],
  ] },
  { id: 'zahlungen', titel: 'Zahlungen', match: /^\/kanzlei\/zahlungen/, kacheln: [
    ['/kanzlei/zahlungen', 'Übersicht', 'Offene Beträge und Filter', LayoutDashboard],
    ['/kanzlei/zahlungen/rechnungen', 'Rechnungen', 'Erstellen, versenden, buchen', FileText, null, false, null, false, 'rechnungen'],
    ['/kanzlei/zahlungen/akonto', 'Akonto', 'Vorschüsse anfordern und zuordnen', Wallet, null, false, null, false, 'akonto'],
    ['/kanzlei/zahlungen/abgleich', 'Zahlungsabgleich', 'Bankeingänge zuordnen', ArrowLeftRight],
    ['/kanzlei/zahlungen/mahnlauf', 'Mahnlauf', 'Mahnstufen und Freigaben', BellRing],
    ['/kanzlei/zahlungen/raten', 'Ratenpläne', 'Raten anbieten und verfolgen', Layers],
    ['/kanzlei/zahlungen/sepa-lastschrift', 'SEPA-Lastschrift', 'Mandate und Einzüge', Landmark],
    ['/kanzlei/zahlungen/notfall', 'Notfall-Pauschalen', 'Pauschalen für Notfälle', Siren, null, false, 'notfall'],
  ] },
  { id: 'admin', titel: 'Admin', match: /^\/(admin\/(analyse|einstellungen|akten|arbeitszeit|schnittstellen|zeitstrahl|zahlungsuebersicht)|kanzlei\/(vorlagen|benutzer|website))/, kacheln: [
    ['/admin/zahlungsuebersicht', 'Zahlungsübersicht', 'Einnahmen, Gebühren, Auszahlungen', Landmark, null, true, null, true],
    ['/admin/einstellungen', 'Einstellungen', 'Kanzlei, Funktionen, Regeln', Settings, null, true, null, true],
    ['/kanzlei/vorlagen', 'Vorlagen', 'Texte und Dokumente', FileStack],
    ['/kanzlei/benutzer', 'Benutzer', 'Team und Rechte', Users],
    ['/admin/arbeitszeit', 'Arbeitszeit Team', 'Zeiten und Urlaub prüfen', Clock, null, true, null, true],
    ['/admin/akten', 'Akten', 'Alle Akten verwalten', FolderArchive, null, true, null, true],
    ['/admin/zeitstrahl', 'Zeitstrahl', 'Alle Ereignisse', History, null, true, null, true],
    ['/admin/analyse', 'Datenanalyse', 'Zahlen und Auswertungen', BarChart3, null, true, null, true],
    ['/admin/schnittstellen', 'Schnittstellen', 'Externe Verbindungen', Plug, null, true, null, true],
    ['/kanzlei/website/aktuelles', 'Website: Aktuelles', 'Beiträge veröffentlichen', Newspaper, /^\/kanzlei\/website\/aktuelles/],
    ['/kanzlei/website/newsletter', 'Newsletter', 'Abonnenten und Versand', Mail],
    ['/kanzlei/website/bewerbungen', 'Bewerbungen', 'Bewerber bearbeiten', Briefcase],
    ['/kanzlei/website/rechtstexte', 'Rechtstexte', 'Impressum, AGB, Datenschutz', Scale],
  ] },
];

export const flaecheFuer = (pathname) => FLAECHEN.find((f) => f.match.test(pathname)) || null;

const Kachel = ({ k, aktiv, n, gezogen }) => {
  const [to, titel, text, Icon] = k;
  const ref = useRef(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: to });
  useEffect(() => { if (aktiv) ref.current?.scrollIntoView({ inline: 'nearest', block: 'nearest' }); }, [aktiv]);
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 20 : undefined };
  return (
    <Link ref={(el) => { ref.current = el; setNodeRef(el); }} style={style} {...attributes} {...listeners} to={to} onClick={(e) => { if (gezogen.current) e.preventDefault(); }}
      aria-current={aktiv ? 'page' : undefined} data-testid={`flaeche-kachel-${to.split('/').filter(Boolean).join('-')}`}
      className={`group relative shrink-0 w-[150px] sm:w-auto flex items-start gap-3 rounded-xl border px-3.5 py-3 touch-manipulation transition-[background-color,border-color,box-shadow] duration-200 ${isDragging ? 'shadow-[0_18px_36px_-16px_rgba(11,23,36,.55)] cursor-grabbing' : 'cursor-pointer'} ${aktiv ? 'bg-[#0B1724] border-[#0B1724] text-white shadow-[0_10px_24px_-14px_rgba(11,23,36,.7)]' : 'bg-white border-[#DCE3EA] text-[#15202B] hover:border-[#b8893d]'}`}>
      <Icon className={`w-[18px] h-[18px] mt-0.5 shrink-0 ${aktiv ? 'text-[#e0b56a]' : 'text-[#b8893d]'}`} strokeWidth={1.75} />
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold leading-tight">{titel}</span>
        <span className={`hidden sm:block text-[11px] leading-snug mt-0.5 ${aktiv ? 'text-white/65' : 'text-[#56616D]'}`}>{text}</span>
      </span>
      <GripVertical className={`hidden sm:block absolute bottom-2 right-1.5 w-3.5 h-3.5 opacity-0 group-hover:opacity-40 transition-opacity ${aktiv ? 'text-white' : 'text-[#56616D]'}`} aria-hidden />
      {n > 0 && <span className="absolute top-2 right-2 min-w-[20px] h-5 px-1.5 rounded-full bg-[#b8893d] text-[#0B1724] text-[11px] font-semibold inline-flex items-center justify-center" data-testid={`flaeche-zahl-${titel.toLowerCase().replace(/[^a-z]/g, '')}`}>{n}</span>}
    </Link>
  );
};

let reihenCache = null;
const useReihenfolge = () => {
  const [reihen, setReihen] = useState(reihenCache || {});
  useEffect(() => {
    if (reihenCache) return;
    api.get('/kanzlei/kachel-reihenfolge').then((r) => { reihenCache = r.data.kacheln || {}; setReihen(reihenCache); }).catch(() => {});
  }, []);
  const speichern = (flaeche, reihenfolge) => {
    reihenCache = { ...(reihenCache || {}), [flaeche]: reihenfolge };
    setReihen(reihenCache);
    api.put('/kanzlei/kachel-reihenfolge', { flaeche, reihenfolge }).catch(() => {});
  };
  return [reihen, speichern];
};

const sortiert = (kacheln, reihe = []) => {
  const pos = (to) => { const i = reihe.indexOf(to); return i < 0 ? 1000 : i; };
  return kacheln.map((k, i) => [k, i]).sort((a, b) => pos(a[0][0]) - pos(b[0][0]) || a[1] - b[1]).map(([k]) => k);
};

// Arbeitsfläche je Bereich: Kacheln oben (per Ziehen sortierbar), die gewählte Funktion öffnet sich direkt darunter
export const Arbeitsflaeche = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const features = useFeatures();
  const zaehler = useMenueZaehler();
  const [reihen, speichern] = useReihenfolge();
  const gezogen = useRef(false);
  const klickSperre = useRef(null);
  const sperren = () => {
    klickSperre.current = (e) => { e.preventDefault(); e.stopPropagation(); };
    window.addEventListener('click', klickSperre.current, { capture: true, once: true });
  };
  const freigeben = () => setTimeout(() => { gezogen.current = false; if (klickSperre.current) window.removeEventListener('click', klickSperre.current, { capture: true }); klickSperre.current = null; }, 60);
  const sensoren = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }), useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 6 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const f = flaecheFuer(pathname);
  if (!f) return null;
  const rolle = user?.role;
  const kacheln = sortiert(f.kacheln.filter(([, , , , , voll, feature, admin]) => (!voll || rolle !== 'freelancer') && (!admin || rolle === 'admin') && (!feature || features[feature])), reihen[f.id]);
  const ids = kacheln.map((k) => k[0]);
  const aktiv = (k) => (k[4] ? k[4].test(pathname) : pathname === k[0]);
  const ende = ({ active, over }) => {
    freigeben();
    if (!over || active.id === over.id) return;
    speichern(f.id, arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)));
  };
  return (
    <section className="pt-[104px] lg:pt-[152px] max-w-[1100px] mx-auto px-4 lg:px-8" data-testid={`flaeche-${f.id}`}>
      <div className="pt-6 flex flex-wrap items-baseline gap-3">
        <h2 className="kp-abschnitt" data-testid="flaeche-titel">{f.titel}</h2>
        <span className="text-[11px] text-[#56616D]">Funktion wählen – sie öffnet sich direkt darunter. Kacheln per Ziehen nach vorne holen.</span>
        {reihen[f.id]?.length > 0 && <button type="button" onClick={() => speichern(f.id, [])} className="text-[11px] text-[#b8893d] hover:text-[#1B3A5C]" data-testid="flaeche-reihenfolge-zuruecksetzen">Reihenfolge zurücksetzen</button>}
      </div>
      <DndContext sensors={sensoren} collisionDetection={closestCenter} onDragStart={() => { gezogen.current = true; sperren(); }} onDragEnd={ende} onDragCancel={freigeben}>
        <SortableContext items={ids} strategy={rectSortingStrategy}>
          <div className="mt-3 flex sm:grid sm:grid-cols-3 lg:grid-cols-4 gap-2.5 overflow-x-auto sm:overflow-visible no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 pb-1" data-testid="flaeche-kacheln">
            {kacheln.map((k) => <Kachel key={k[0]} k={k} aktiv={aktiv(k)} n={k[8] ? zaehler[k[8]] : 0} gezogen={gezogen} />)}
          </div>
        </SortableContext>
      </DndContext>
    </section>
  );
};

export default Arbeitsflaeche;
